import {useEffect} from 'react';
import {updateUserPreferences} from '@/reducers/user-preferences.reducer';
import {
  updateIsPosterEditorMobileVariant,
  updateIsPosterShared,
  updateIsPosterSharedInTeam,
  updateRecordOptionsOpenState,
  updateTemplatesPopupOpenState,
} from '@Components/poster-editor/poster-editor-reducer';
import {handleTemplatesPopupOpenStateForMobile, initComWithIframe, isEditorMobileVariant, setMultiPageFlagInRedux} from '@Components/poster-editor/library/poster-editor-library';
import {openRecordAudioModal, RECORD_AUDIO_PANEL_ID} from '@Modals/record-audio-modal';
import {watchIsAudioPlayable} from '@Utils/audio.util';
import {bindPosterEditorShortcuts, unbindPosterEditorShortcuts} from '@PosterWhiteboard/poster/poster-editor-shortcuts';
import type {PosterEditorConfig} from '@Components/poster-editor/poster-editor.types';
import {useAppDispatch} from '@/hooks';
import {updateCurrentUserToRedux} from '@Utils/user.util';
import type {ElementData} from '@Libraries/add-media-library';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {handlePasteOnPosterEditorCanvas} from '@Components/poster-editor/library/poster-editor-copy-paste-library';
import {getRecentStockItems} from '@Libraries/pmw-stock-media-library';
import {setRecentStockAvailability} from '@Panels/stock-panel/stock-panel-reducer';
import {MIN_STOCK_ITEMS_TO_DISPLAY} from '@Panels/stock-panel/stock-panel.types';

interface PosterEditorInitProps {
  posterEditorConfig: PosterEditorConfig;
}

export const useInitPosterEditor = (data: PosterEditorInitProps): void => {
  const dispatch = useAppDispatch();
  const onCloseAudioModel = useDefaultPanelClose(RECORD_AUDIO_PANEL_ID);

  const onAddRecordedItems = (item: ElementData): void => {
    onAddItems([item]);
  };

  const onCloseAudioRecordModal = (): void => {
    dispatch(updateRecordOptionsOpenState(true));
    onCloseAudioModel();
  };

  const initializeRecentStockItemFlag = async (): Promise<void> => {
    try {
      const response = await getRecentStockItems();
      dispatch(setRecentStockAvailability(response && response.length >= MIN_STOCK_ITEMS_TO_DISPLAY));
    } catch {
      dispatch(setRecentStockAvailability(false));
    }
  };

  useEffect(() => {
    if (window.posterEditor) {
      window.posterEditor.config = data.posterEditorConfig;
    }
    dispatch(updateUserPreferences(data.posterEditorConfig.userPreferences));
    dispatch(updateTemplatesPopupOpenState(data.posterEditorConfig.shouldOpenTemplatePopup ?? false));
    dispatch(updateIsPosterShared(data.posterEditorConfig.isPosterShared ?? false));
    dispatch(updateIsPosterSharedInTeam(data.posterEditorConfig.isPosterSharedInTeam ?? false));
    handleTemplatesPopupOpenStateForMobile(data.posterEditorConfig.shouldOpenTemplatePopup ?? false);
    if (data.posterEditorConfig.shouldOpenAudioRecordingPanel) {
      openRecordAudioModal({onAddSelected: onAddRecordedItems, onClose: onCloseAudioRecordModal});
    }
    updateCurrentUserToRedux();
    watchIsAudioPlayable();
    initComWithIframe();
    window.addEventListener('paste', handlePasteOnPosterEditorCanvas);
    bindPosterEditorShortcuts();
    void initializeRecentStockItemFlag();
    setMultiPageFlagInRedux();
    dispatch(updateIsPosterEditorMobileVariant(isEditorMobileVariant()));

    return (): void => {
      window.removeEventListener('paste', handlePasteOnPosterEditorCanvas);
      unbindPosterEditorShortcuts();
    };
  }, []);
};
